// =============================================================================
// Talent Hub (professional side): pure helpers for SearchJobsPage
// =============================================================================
// The server is the source of truth for jobs, applications and preferences
// (packages/backend/src/jobs). These helpers only shape what it sends and
// what the page sends back. They never fill in a value nobody gave: the page
// used to show and send invented ones ("Demo Candidate", "Chennai",
// "Infosys Ltd.", a salary range, "100% Match", made-up file names).

/** Shown where a job does not give a detail. */
export const NOT_GIVEN = "Not given";

/** The server's limit for a CV or resume (application-files.ts). */
export const MAX_APPLICATION_FILE_BYTES = 5 * 1024 * 1024;

/** The files the server takes as a CV or resume, for the file picker. */
export const APPLICATION_FILE_TYPES =
  ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png";

const CARD_NAMES = ["card1", "card2", "card3", "card4"];

// Accounts created before 25 Sep 2026 may carry this stand-in number (a
// recorded finding); it is never prefilled or sent as the professional's.
const PLACEHOLDER_PHONES = new Set(["9876543210"]);

const text = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const isPlainObject = (value) =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** The first value given, as trimmed text, or null when none is. */
function firstGiven(...values) {
  const found = values.map(text).find(Boolean);
  return found || null;
}

/** The four preference cards with nothing chosen. */
export function emptyPreferenceCards() {
  return {
    card1: {
      applyingFor: "",
      name: "",
      dob: "",
      gender: "",
      languagesKnown: "",
      educationLevel: "",
      requiredDegree: "",
      educationalInstitution: "",
      certifications: "",
      militaryVeteran: "",
      industry: "",
      industryId: "",
      preferredIndustryIds: [],
      industryNonNegotiable: false,
      experienceNonNegotiable: false,
      employmentTypeNonNegotiable: false,
      department: "",
      skillset: "",
      experienceYears: "",
      employmentType: "",
      multipleOpenings: "",
      contactNumber: "",
      email: "",
      address: "",
      resumeFile: "",
      cvFile: "",
      resumeDataUrl: "",
      cvDataUrl: "",
    },
    card2: {
      minSalary: "",
      maxSalary: "",
      joiningBonusImportant: "",
      variablePayOpen: "",
      healthInsuranceImportant: "",
      statutoryBenefitsImportant: "",
      valueESOPs: "",
      valueLD: "",
      noticePeriod: "",
      noticePeriodNonNegotiable: false,
      minStayMonths: "",
    },
    card3: {
      workingModel: "",
      currentLocation: "",
      preferredLocation: "",
      locationNonNegotiable: false,
      willingToRelocate: "",
      willingLocalTravel: "",
      willingCountryTravel: "",
      willingIntlTravel: "",
      preferredShift: "",
      maxHoursPerDay: "",
      flexibleWorkHours: "",
      extendBeyondShift: "",
      willingWeekends: "",
      teamSizeReporting: "",
      probationPeriod: "",
      interviewMode: "",
      backgroundVerification: "",
      visaSponsorship: "",
      laptopProvided: "",
    },
    card4: {
      cabFacilityImportant: "",
      cafeteriaImportant: "",
      leavePolicyImportant: "",
      safetyProvisionsImportant: "",
      mentalHealthImportant: "",
      diPoliciesImportant: "",
      teamCultureStyle: "",
      dressCode: "",
    },
  };
}

/** A phone number as given, unless it is the recorded stand-in. */
export function realPhone(value) {
  const phone = text(value);
  const lastTen = phone.replaceAll(/\D/g, "").slice(-10);
  return PLACEHOLDER_PHONES.has(lastTen) ? "" : phone;
}

/**
 * What the signed-in account says about the person, for the contact fields
 * of the first card: its name, email, mobile number and city. Empty where
 * the account does not say.
 */
export function accountDetails(user) {
  const person = user?.professional ?? user;
  return {
    name: [person?.firstName, person?.lastName]
      .map(text)
      .filter(Boolean)
      .join(" "),
    email: text(user?.email),
    contactNumber: realPhone(user?.phone),
    address: [user?.professional?.city, user?.professional?.state]
      .map(text)
      .filter(Boolean)
      .join(", "),
  };
}

/** The first card with its empty contact fields filled in from the account. */
function withAccountDetails(card1, user) {
  const filled = { ...card1 };
  for (const [field, value] of Object.entries(accountDetails(user))) {
    if (value && !text(filled[field])) filled[field] = value;
  }
  return filled;
}

/** A stored card without its empty (null) fields. */
function givenFields(card) {
  if (!isPlainObject(card)) return {};
  return Object.fromEntries(
    Object.entries(card).filter(
      ([, value]) => value !== null && value !== undefined,
    ),
  );
}

/**
 * The cards to edit: nothing chosen, then what the server holds (`saved`,
 * null before it has answered), then the account's contact details where
 * the first card has none.
 */
export function preferencesToEdit(saved, user) {
  const empty = emptyPreferenceCards();
  const cards = {};
  for (const name of CARD_NAMES) {
    cards[name] = { ...empty[name], ...givenFields(saved?.[name]) };
  }
  cards.card1 = withAccountDetails(cards.card1, user);
  return cards;
}

/** What Reset saves: every card emptied, the account's contact details kept. */
export function resetPreferenceCards(user) {
  const cards = emptyPreferenceCards();
  cards.card1 = withAccountDetails(cards.card1, user);
  return cards;
}

/** "12,00,000 - 18,00,000" from the amounts given; null with none (as the server). */
function salaryRangeText(min, max) {
  const low = text(min);
  const high = text(max);
  if (low && high) return `${low} - ${high}`;
  if (low) return `From ${low}`;
  if (high) return `Up to ${high}`;
  return null;
}

/** "3-6 years" from the years given; null with none (as the server). */
function experienceRangeText(min, max) {
  const low = text(min);
  const high = text(max);
  if (low && high) return `${low}-${high} years`;
  if (low) return `${low}+ years`;
  if (high) return `Up to ${high} years`;
  return null;
}

/**
 * What a job says about itself. Each detail is null when the organisation
 * did not give it: the page shows NOT_GIVEN, never a stand-in.
 */
export function jobFacts(job) {
  const card1 = isPlainObject(job?.card1) ? job.card1 : {};
  const card2 = isPlainObject(job?.card2) ? job.card2 : {};
  const card3 = isPlainObject(job?.card3) ? job.card3 : {};
  return {
    location: firstGiven(job?.location, card3.jobLocation),
    workModel: firstGiven(job?.workModel, card3.workingModel),
    salary: firstGiven(
      job?.salary,
      salaryRangeText(card2.minSalary, card2.maxSalary),
    ),
    experience: firstGiven(
      job?.expRequired,
      experienceRangeText(card1.minExpYears, card1.maxExpYears),
    ),
    noticePeriod: firstGiven(card2.noticePeriod),
    requiredDegree: firstGiven(card1.requiredDegree),
    essentialSkillset: firstGiven(card1.essentialSkillset),
  };
}

/** The professional's application to a job, when they have applied. */
export function applicationForJob(applications, jobId) {
  if (!Array.isArray(applications) || !text(jobId)) return undefined;
  return applications.find((app) => String(app?.jobId) === String(jobId));
}

/** When the application was made, as the server recorded it, or null. */
export function applicationDate(application) {
  if (text(application?.appliedAt)) return text(application.appliedAt);
  const created = new Date(application?.createdAt ?? "");
  if (Number.isNaN(created.getTime())) return null;
  return created.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

/** "3-4 years", "Fresher", "over 35 years": experience as a recruiter reads it. */
export function experienceText(value) {
  const chosen = text(value);
  return /^\d[\d-]*$/.test(chosen) ? `${chosen} years` : chosen;
}

/** "UG (B.Sc Physics)", "UG" or "B.Sc Physics", from what was given. */
function educationText(card1) {
  const level = text(card1.educationLevel);
  const degree = text(card1.requiredDegree);
  if (level && degree) return `${level} (${degree})`;
  return level || degree;
}

/**
 * What the server stores with an application: only what the professional
 * gave in their preferences. The account, its email, the status and the
 * verified flag come from the session on the server, and a CV or resume
 * goes only when one was attached.
 */
export function applicationPayload(cards) {
  const card1 = cards?.card1 ?? {};
  const card2 = cards?.card2 ?? {};
  const card3 = cards?.card3 ?? {};
  const payload = {
    candidateName: text(card1.name),
    contactNumber: realPhone(card1.contactNumber),
    education: educationText(card1),
    institution: text(card1.educationalInstitution),
    skillset: text(card1.skillset),
    experience: experienceText(card1.experienceYears),
    location: text(card3.currentLocation),
    noticePeriod: text(card2.noticePeriod),
  };
  if (text(card1.resumeDataUrl)) {
    payload.resumeFile = text(card1.resumeFile);
    payload.resumeDataUrl = card1.resumeDataUrl;
  }
  if (text(card1.cvDataUrl)) {
    payload.cvFile = text(card1.cvFile);
    payload.cvDataUrl = card1.cvDataUrl;
  }
  return payload;
}

/** An application as the page holds it: the files stay on the server. */
export function withoutFiles(application) {
  const copy = { ...application };
  delete copy.cvDataUrl;
  delete copy.resumeDataUrl;
  return copy;
}

/** Why a chosen CV or resume cannot be sent, or null when it can be. */
export function applicationFileProblem(file) {
  if (!file) return null;
  if (file.size > MAX_APPLICATION_FILE_BYTES) {
    return `${file.name} is larger than 5 MB. Attach a file of up to 5 MB.`;
  }
  return null;
}

/**
 * A list read a page at a time, with the next page added: only the items
 * not already held, since a job posted meanwhile shifts the pages by one.
 */
export function appendPage(held, page) {
  const list = Array.isArray(held) ? held : [];
  const ids = new Set(list.map((item) => String(item.id)));
  const fresh = (Array.isArray(page) ? page : []).filter(
    (item) => !ids.has(String(item.id)),
  );
  return [...list, ...fresh];
}

/** A full page means the server may hold more. */
export function mayHaveMore(page, pageSize) {
  return Array.isArray(page) && page.length >= pageSize;
}
